import React, { useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-banned-imports
import { useId } from '@atlaskit/ds-lib/use-id';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	getInputSkeletonFocus,
	resetInputSkeletonState,
	setInputSkeletonIsFocus,
	setInputSkeletonQuery,
	setInputSkeletonSelection,
} from '@atlassian/search-dialog/query-store';
import { SEARCH_QUERY_KEY, setSessionItem } from '@atlassian/search-dialog/session-storage-utils';

import { Textfield } from '../../../common/ui/quick-find/textfield';
import { getInitialQuery } from '../../../common/ui/quick-find/textfield/input-skeleton';
import {
	type GhostTextData,
	parseGhostTextData,
} from '../../../common/utils/quick-find/ghost-text';

import { messages } from './messages';

export type QuickFindSkeletonProps = {
	onEnter?: (e: React.KeyboardEvent) => void;
	isNav4Enabled?: boolean;
	injectValue?: string;
	customPlaceholder?: string;
	inputRef?: React.RefObject<HTMLInputElement>;
};

if (!window.inputSkeletonState) {
	resetInputSkeletonState();
}

export const QuickFindSkeleton = ({
	onEnter,
	isNav4Enabled,
	injectValue,
	customPlaceholder,
	inputRef,
}: QuickFindSkeletonProps): React.JSX.Element => {
	const dialogId = useId();
	const { formatMessage, locale } = useIntl();
	const initialSearchQuery = getInitialQuery();

	// Use the stored state as initial values for expansion and query
	const [isExpanded, setIsExpanded] = useState(
		fg('quick_find_no_expand_on_keyboard_focus') ? getInputSkeletonFocus() : getInputSkeletonFocus,
	);
	const [value, setValue] = useState(() => injectValue ?? initialSearchQuery);

	const ghostTextData = useMemo(
		(): GhostTextData | undefined => parseGhostTextData(customPlaceholder, locale),
		[customPlaceholder, locale],
	);

	const onFocus = (e: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLInputElement>) => {
		setIsExpanded(true);
		setInputSkeletonIsFocus(true);
		const target = e.currentTarget;
		setInputSkeletonSelection(
			target.selectionStart || 0,
			target.selectionEnd || 0,
			target.selectionDirection || 'none',
		);
	};

	const onBlur = () => {
		setIsExpanded(false);
		setInputSkeletonIsFocus(false);
		setInputSkeletonSelection(0, 0, 'none');
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		const target = e.currentTarget;
		if (e.key === 'Escape') {
			target.blur();
			onBlur();
		}
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.currentTarget.value;
		setValue(newValue);
		setInputSkeletonQuery(newValue);
		const target = e.currentTarget;
		setInputSkeletonSelection(
			target.selectionStart || 0,
			target.selectionEnd || 0,
			target.selectionDirection || 'none',
		);
		setSessionItem(SEARCH_QUERY_KEY, newValue);
	};

	const onSelect = (e: React.UIEvent<HTMLInputElement>) => {
		const target = e.currentTarget;
		setInputSkeletonSelection(
			target.selectionStart || 0,
			target.selectionEnd || 0,
			target.selectionDirection || 'none',
		);
	};

	return (
		<Textfield
			value={value}
			placeholder={
				fg('quickfind-ghosttext')
					? (ghostTextData?.text ?? formatMessage(messages.placeholder))
					: formatMessage(messages.placeholder)
			}
			dialogId={dialogId}
			quickFindOpen={isExpanded}
			onEnter={onEnter}
			onClick={fg('quick_find_no_expand_on_keyboard_focus') ? onFocus : undefined}
			onFocus={onFocus}
			onBlur={onBlur}
			onSelect={onSelect}
			onKeyDown={onKeyDown}
			onChange={onChange}
			isNav4Enabled={isNav4Enabled}
			inputRef={inputRef}
		/>
	);
};
